import type { SceneEditor } from '@zakeke/zakeke-3d-core';
import { create } from 'zustand';
import useComposerStore from '../composerStore';
import { ComposerAttributeNodeAdapter } from './composerNodesAdapter';
import { setActionSceneElementType } from './composerHelpers';
import {
    ComposerAttributeOptionNode,
    ComposerMaterialsColorType,
    ComposerOptionActionNode,
    ComposerOptionActionsType
} from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';

// LA SCENA PRIMA DEL PRIMO PLAY (utente, 2026-10-20): il motore non sa tornare indietro, e rieseguire le opzioni
// predefinite non basta — lo stato di partenza di un oggetto viene dalla scena, non da un'opzione. Si fotografa ciò
// che un'azione può cambiare: la visibilità e il materiale di ogni mesh, i colori dei materiali, la camera. I
// parametri di un materiale (Edit Material) restano fuori: sono troppi per fotografarli tutti.
type SceneSnapshot = {
    meshes: { id: string; visible: boolean; material?: string }[];
    colors: { id: string; property: string; hex: string }[];
    camera?: { alpha: number; beta: number; radius: number; target: BABYLON.Vector3 };
};

const COLOR_PROPERTIES = Object.values(ComposerMaterialsColorType) as string[];

// DOPO UN PLAY LA SCENA NON È PIÙ QUELLA DI PARTENZA: lo si ricorda qui, e la testata dell'elenco mostra Reset.
export const useComposerPlayed = create<{
    played: boolean;
    snapshot: SceneSnapshot | null;
    setPlayed: (played: boolean, snapshot?: SceneSnapshot | null) => void;
}>((set) => ({
    played: false,
    snapshot: null,
    setPlayed: (played, snapshot = null) => set({ played, snapshot })
}));

const takeSnapshot = (sceneEditor: SceneEditor): SceneSnapshot | null => {
    const scene = sceneEditor.scene;
    if (!scene) return null;
    const camera = scene.activeCamera as BABYLON.ArcRotateCamera | null;
    return {
        meshes: scene.meshes.map((mesh) => ({ id: mesh.id, visible: mesh.isEnabled() && mesh.isVisible, material: mesh.material?.id })),
        colors: scene.materials.flatMap((material) =>
            COLOR_PROPERTIES.filter((property) => (material as any)[property] instanceof BABYLON.Color3).map((property) => ({
                id: material.id,
                property,
                hex: ((material as any)[property] as BABYLON.Color3).toHexString()
            }))
        ),
        camera: camera?.target ? { alpha: camera.alpha, beta: camera.beta, radius: camera.radius, target: camera.target.clone() } : undefined
    };
};

const sceneActionsOf = (actions: ComposerOptionActionNode[], onlyEnabled = true) =>
    [...actions]
        .filter((action) => !onlyEnabled || action.enabled !== false)
        .sort((left, right) => left.displayOrder - right.displayOrder)
        .map((action) => ComposerAttributeNodeAdapter.fromActionNode(action));

/**
 * Una o più azioni di un'opzione sul modello: al primo Play fotografa la scena, così Reset la riporta com'era. Il Play
 * di una card la prova anche spenta (`onlyEnabled` falso): è il modo di vedere che cosa farebbe.
 */
export const playComposerActions = (
    sceneEditor: SceneEditor | null | undefined,
    attributeIDNumeric: number,
    actions: ComposerOptionActionNode[],
    onlyEnabled = true
) => {
    const sceneActions = sceneActionsOf(actions, onlyEnabled);
    if (!sceneEditor || !sceneActions.length) return;
    const state = useComposerPlayed.getState();
    if (!state.played) state.setPlayed(true, takeSnapshot(sceneEditor));
    void sceneEditor.executeActionsAsync(attributeIDNumeric, null, sceneActions);
};

/**
 * MOSTRA UN'OPZIONE SUL MODELLO (prototipo, Play della riga e della testata): le sue azioni accese, nel loro ordine,
 * come le eseguirebbe il configuratore scegliendola.
 */
export const playComposerOption = (sceneEditor: SceneEditor | null | undefined, option: ComposerAttributeOptionNode) => {
    const attribute = useComposerStore.getState().attributes.find((candidate) => candidate.id === option.attributeID);
    if (attribute) playComposerActions(sceneEditor, attribute.idNumeric, option.actions);
};

// Un'azione di servizio, nella stessa forma di quelle delle opzioni: il motore la esegue come le altre.
const restoreAction = (type: ComposerOptionActionsType, selector: string, value: string, property = '') =>
    ComposerAttributeNodeAdapter.fromActionNode(
        setActionSceneElementType({
            ...ComposerAttributeNodeAdapter.toNodeAction('', '', ComposerAttributeNodeAdapter.createNewAction()),
            type,
            selector,
            value,
            property,
            enabled: true
        })
    );

/**
 * RESET DOPO IL PLAY (prototipo; utente 2026-10-20): la scena torna com'era prima del primo Play. Si rimette solo ciò
 * che è cambiato, con le stesse azioni del motore (mostra / nascondi, materiale, colore), poi la camera.
 */
export const resetComposerPlay = async (sceneEditor: SceneEditor | null | undefined) => {
    const { snapshot, setPlayed } = useComposerPlayed.getState();
    if (!sceneEditor || !snapshot) return setPlayed(false);
    const scene = sceneEditor.scene;
    if (!scene) return setPlayed(false);
    const restores: MPlaza.AttributeSceneAction[] = [];
    snapshot.meshes.forEach(({ id, visible, material }) => {
        const mesh = scene.getMeshById(id);
        if (!mesh) return;
        if ((mesh.isEnabled() && mesh.isVisible) !== visible)
            restores.push(restoreAction(ComposerOptionActionsType.SHOW_OBJECTS, id, visible ? 'show' : 'hide'));
        if (material && mesh.material?.id !== material) restores.push(restoreAction(ComposerOptionActionsType.SET_OBJECT_MATERIAL, id, material));
    });
    snapshot.colors.forEach(({ id, property, hex }) => {
        const color = (scene.getMaterialById(id) as any)?.[property] as BABYLON.Color3 | undefined;
        if (color && color.toHexString() !== hex) restores.push(restoreAction(ComposerOptionActionsType.SET_MATERIAL_COLOR, id, hex, property));
    });
    if (restores.length) await sceneEditor.executeActionsAsync(null, null, restores);
    const camera = scene.activeCamera as BABYLON.ArcRotateCamera | null;
    if (camera && snapshot.camera && camera.target) {
        camera.alpha = snapshot.camera.alpha;
        camera.beta = snapshot.camera.beta;
        camera.radius = snapshot.camera.radius;
        camera.setTarget(snapshot.camera.target);
    }
    setPlayed(false);
};
